import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Sparkles, Landmark, CheckCircle2, Lock, Mail } from "lucide-react";
import { toast } from "sonner";
import { api } from "../api";
import { PageHeader, GlassCard, ScoreRing, ProgressBar, Spinner, Disclaimer, Chip } from "../components/ui";

export default function Funding() {
  const [data, setData] = useState(null);
  const [lenders, setLenders] = useState(null);

  useEffect(() => {
    api.get("/funding/readiness").then(({ data }) => setData(data)).catch(() => setData(false));
    api.get("/funding/lenders").then(({ data }) => setLenders(data)).catch(() => setLenders(false));
  }, []);

  if (data === null) return <Spinner />;
  if (data === false) return <div className="text-slate-400">Could not load funding readiness.</div>;

  return (
    <div className="space-y-6">
      <PageHeader
        label="Educational Score"
        title="Funding Readiness"
        subtitle="A composite educational score based on your foundation, credit, banking, and documentation."
        action={<button onClick={async () => { try { await api.post("/funding/email-report"); toast.success("Report emailed to you"); } catch { toast.error("Could not email report"); } }} data-testid="funding-email-report" className="inline-flex items-center gap-2 rounded-xl border border-white/12 px-3.5 py-2 text-sm text-slate-300 cc-nav-item"><Mail size={15} /> Email me this report</button>}
      />

      <div className="grid lg:grid-cols-3 gap-5">
        <GlassCard className="p-8 flex flex-col items-center justify-center text-center">
          <ScoreRing value={data.score} label="Ready" size={168} stroke={12} />
          <p className="text-slate-400 text-sm mt-5 max-w-xs">
            This score reflects how organized and prepared your profile is — for educational purposes only.
          </p>
        </GlassCard>

        <GlassCard className="p-6 lg:col-span-2">
          <h3 className="cc-heading text-lg font-500 text-white mb-5">Readiness Factors</h3>
          <div className="space-y-4">
            {data.factors.map((f, i) => (
              <motion.div key={f.label}
                initial={{ opacity: 0, x: -10 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: i * 0.05 }}>
                <div className="flex items-center justify-between mb-1.5">
                  <span className="text-sm text-slate-300">{f.label}</span>
                  <span className="text-sm text-slate-400">{f.value}%</span>
                </div>
                <ProgressBar value={f.value} />
              </motion.div>
            ))}
          </div>
        </GlassCard>
      </div>

      {data.recommendations.length > 0 && (
        <GlassCard className="p-6">
          <div className="flex items-center gap-2 mb-4">
            <Sparkles size={17} className="text-purple-300" />
            <h3 className="cc-heading text-lg font-500 text-white">AI Recommendations</h3>
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            {data.recommendations.map((r, i) => (
              <div key={i} className="rounded-xl cc-surface border border-white/[0.05] px-4 py-3 text-sm text-slate-300">{r}</div>
            ))}
          </div>
        </GlassCard>
      )}

      <Disclaimer text={data.disclaimer} />

      {lenders && lenders.programs && (
        <GlassCard className="p-6">
          <div className="flex items-center gap-2 mb-1">
            <Landmark size={17} className="text-blue-300" />
            <h3 className="cc-heading text-lg font-500 text-white">Matched Programs</h3>
          </div>
          <p className="text-sm text-slate-400 mb-4">{lenders.note}</p>
          <div className="grid sm:grid-cols-2 gap-3">
            {lenders.programs.map((p) => (
              <div key={p.name} className={`rounded-xl border p-4 ${p.eligible ? "border-emerald-500/25 bg-emerald-500/[0.05]" : "border-white/[0.05] cc-surface"}`} data-testid={`lender-${p.name.replace(/\s+/g, "-").toLowerCase()}`}>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-white font-500">{p.name}</span>
                  {p.eligible ? <Chip tone="green"><CheckCircle2 size={12} /> Eligible</Chip> : <Chip><Lock size={12} /> {p.min_readiness}+ needed</Chip>}
                </div>
                <div className="mt-2 flex items-center justify-between text-xs text-slate-400">
                  <span>{p.type}</span><span>{p.amount}</span>
                </div>
              </div>
            ))}
          </div>
        </GlassCard>
      )}
    </div>
  );
}
